import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { OverflowMode, TabComponent } from "@syncfusion/ej2-react-navigations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";
import { useRouteContainer } from "../Routing/RouteContainerStateContext";
import classNames from "classnames";
import { utils } from "../..";

export enum TabbedCardOrientation {
  Horizontal = "horizontal",
  Vertical = "vertical",
}

export interface TabbedCardProps {
  className?: string;
  tabs: TabbedCardContent[];
  headerActions?: {
    icon?: IconDefinition;
    text?: string;
    onClick?: () => void;
    visibleForTab?: number; // Index of the tab where this action should be visible
  }[];
  orientation?: TabbedCardOrientation;
  showExpandIcon?: boolean;
  /**
   * Optional CSS selector that identifies the element whose bounds the card should expand within.
   * If omitted the card retains its full-viewport expansion behaviour.
   */
  expandWithin?: string;
  activeTabKey?: string;
  defaultTabKey?: string;
  basePath?: string;
  onTabChange?: (tabKey: string) => void;
  overflowMode?: OverflowMode;
}

export interface TabbedCardContent {
  tabKey: string;
  headerContent: ReactNode | string;
  tabContent: ReactNode;
  disabled?: boolean;
}

const TabbedCard: FC<TabbedCardProps> = (props) => {
  if (props.defaultTabKey && props.activeTabKey) {
    throw new Error(
      "defaultTabKey and activeTabKey cannot be used together. If you provide an activeTabKey, you are indicating this is a controlled component, and that the state of the tab selection will be passed from the parent. If you provide a defaultTabKey, then the tab selection will be controlled by the component itself."
    );
  }

  // Determine initial tab - use activeTabKey if provided, otherwise first tab
  const getInitialTabIdx = () => {
    if (props.activeTabKey) {
      const idx = props.tabs.findIndex(
        (tab) => tab.tabKey === props.activeTabKey
      );
      return idx !== -1 ? idx : 0;
    }
    if (props.defaultTabKey) {
      const idx = props.tabs.findIndex(
        (tab) => tab.tabKey === props.defaultTabKey
      );
      return idx !== -1 ? idx : 0;
    }
    return 0;
  };

  const [selectedTabIdx, setSelectedTabIdx] = useState(getInitialTabIdx);
  const selectedTabKey = props.tabs[selectedTabIdx]?.tabKey;
  const [isExpanded, setIsExpanded] = useState(false);
  const tabComponentRef = useRef<TabComponent>(null);
  const tabKeys = props.tabs.map((tab) => tab.tabKey);
  const { activeKey, navigateToContainer } = useRouteContainer(
    props.basePath || "",
    tabKeys
  );

  const orientation = props.orientation || TabbedCardOrientation.Horizontal;

  const handleTabSelected = (e: any) => {
    if (e.selectedIndex !== selectedTabIdx) {
      const tab = props.tabs[e.selectedIndex];

      if (tab.disabled) {
        console.error("Tab is disabled", tab);
        return;
      } else {
        setSelectedTabIdx(e.selectedIndex);
        navigateToContainer(tab.tabKey);
        // Call the onTabChange callback if provided
        props.onTabChange?.(tab.tabKey);

        // Scroll to the top of the tab content
        const tabHeader =
          tabComponentRef.current?.element.querySelector(".e-content");
        if (tabHeader) {
          utils.miscellaneous.scrollToTop(tabHeader as HTMLElement);
        }
      }
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (!props.expandWithin) return;

    const targetElement = document.querySelector<HTMLElement>(
      `.${props.expandWithin}`
    );

    if (!targetElement) return;

    // Ensure the container provides a containing block for absolutely-positioned children
    const originalPosition = targetElement.style.position;

    if (isExpanded) {
      if (originalPosition === "" || originalPosition === "static") {
        targetElement.style.position = "relative";
      }
    } else {
      // Restore original positioning once collapsed
      targetElement.style.position = originalPosition;
    }

    return () => {
      targetElement.style.position = originalPosition;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, props.expandWithin]);

  // Compute inline style overrides when expanded inside a container
  const expandedStyleWithin =
    isExpanded && props.expandWithin
      ? {
          position: "absolute" as const,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          height: "100%",
        }
      : undefined;

  // Sync internal state with external activeTabKey prop
  useEffect(() => {
    if (props.activeTabKey) {
      const newTabIdx = props.tabs.findIndex(
        (tab) => tab.tabKey === props.activeTabKey
      );
      if (newTabIdx !== -1 && newTabIdx !== selectedTabIdx) {
        setSelectedTabIdx(newTabIdx);
      }
    }
  }, [props.activeTabKey, props.tabs, selectedTabIdx]);

  useEffect(() => {
    if (activeKey) {
      const tabIndex = props.tabs.findIndex((tab) => tab.tabKey === activeKey);

      if (tabIndex !== -1) {
        const tab = props.tabs[tabIndex];
        if (tab.disabled) {
          console.error("Tab is disabled", tab);
          return;
        } else {
          setSelectedTabIdx(tabIndex);
          // Call the onTabChange callback if provided
          props.onTabChange?.(tab.tabKey);
        }
      }
    }
  }, [activeKey, props.tabs, props.onTabChange]);

  return (
    <div
      className={classNames("ucl-tabbed-card", props.className, {
        expanded: isExpanded,
        "ucl-tabbed-card-vertical":
          orientation === TabbedCardOrientation.Vertical,
      })}
      style={expandedStyleWithin}
    >
      <TabComponent
        ref={tabComponentRef}
        overflowMode={props.overflowMode || "MultiRow"}
        animation={{
          previous: { effect: "FadeIn" },
          next: { effect: "FadeIn" },
        }}
        headerPlacement={
          orientation === TabbedCardOrientation.Vertical ? "Left" : "Top"
        }
        selected={handleTabSelected}
        selectedItem={selectedTabIdx}
      >
        <div id="tab-header" className="e-tab-header">
          {props.tabs.map((x) => (
            <div
              key={x.tabKey}
              className={classNames("e-header-item", {
                "ucl-tab-header-disabled": x.disabled ?? false,
              })}
            >
              {x.headerContent}
            </div>
          ))}
        </div>
        <div className="e-content">
          {props.tabs.map((x) => (
            <div
              key={x.tabKey}
              className={classNames("e-content-item", "e-item", {
                "e-active": x.tabKey === selectedTabKey,
              })}
            >
              <div className="e-content-wrapper">
                <div className="e-content-container">
                  <div className="ucl-tabbed-card-content">
                    {x.tabKey === selectedTabKey ? (
                      <>
                        {props.showExpandIcon && (
                          <span
                            className="e-expand-icon e-expand-icon-top"
                            onClick={toggleExpand}
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faCompress : faExpand}
                              size="xl"
                            />
                          </span>
                        )}
                        {x.tabContent}
                        {props.showExpandIcon && (
                          <span
                            className="e-expand-icon e-expand-icon-bottom"
                            onClick={toggleExpand}
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faCompress : faExpand}
                              size="xl"
                            />
                          </span>
                        )}
                      </>
                    ) : (
                      <div className="ucl-tabbed-card-content-none"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </TabComponent>
      {props.headerActions && (
        <div className="e-tab-header-action">
          {props.headerActions
            .filter(
              (action) =>
                action.visibleForTab === undefined ||
                action.visibleForTab === selectedTabIdx
            )
            .map((action, index) => (
              <div
                key={index}
                className="e-tab-header-action-item"
                onClick={action.onClick}
              >
                {action.icon && (
                  <span className="e-tab-header-action-icon">
                    <FontAwesomeIcon icon={action.icon} />
                  </span>
                )}
                {action.text && (
                  <span className="e-tab-header-action-text">
                    {action.text}
                  </span>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default TabbedCard;
